// Sticker Studio data model: what a sticker is, the print choices a shop can make, and factories for new layers.
export const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-3);

export const STICKER_TYPES = [
  { id: "die-cut", name: "Die-cut", w: 4, h: 4, shape: "contour", hint: "Cut through vinyl and backing, following the artwork" },
  { id: "kiss-cut", name: "Kiss-cut", w: 4, h: 4, shape: "contour", hint: "Cut through the vinyl only — peels off a square backing" },
  { id: "sheet", name: "Sticker sheet", w: 3, h: 3, shape: "contour", copies: 6, hint: "Several copies kiss-cut on one sheet" },
  { id: "roll", name: "Roll labels", w: 3, h: 2, shape: "rounded", copies: 24, sheet: "roll24", hint: "Repeated down a roll with a gap between labels" },
  { id: "bumper", name: "Bumper", w: 11.5, h: 3, shape: "rounded", radius: 0.375, hint: "Rounded rectangle, 11½ × 3 in — the classic" },
  { id: "window", name: "Clear window", w: 6, h: 4, shape: "contour", material: "clear", hint: "Printed on clear vinyl so the glass shows through" },
];

// bg: flat color, or a named look drawn with SVG/canvas patterns. opaque = white ink / white backing under the print.
export const MATERIALS = [
  { id: "white-gloss", name: "White vinyl · gloss", bg: "#ffffff", opaque: true, sheen: 0.35 },
  { id: "white-matte", name: "White vinyl · matte", bg: "#f6f6f3", opaque: true, sheen: 0 },
  { id: "clear", name: "Clear vinyl", bg: "clear", opaque: false, sheen: 0.25 },
  { id: "holographic", name: "Holographic", bg: "holo", opaque: true, sheen: 0.5 },
  { id: "glitter", name: "Glitter", bg: "glitter", opaque: true, sheen: 0.3 },
  { id: "reflective", name: "Reflective", bg: "#d6dbe2", opaque: true, sheen: 0.7 },
  { id: "chrome", name: "Mirror chrome", bg: "chrome", opaque: true, sheen: 0.8 },
  { id: "kraft", name: "Kraft paper", bg: "#c89f68", opaque: true, sheen: 0 },
];
export const FINISHES = [["none", "No laminate"], ["gloss", "Gloss laminate"], ["matte", "Matte laminate"], ["uv", "UV laminate · outdoor 5 yr"]];
export const SHAPES = [["contour", "Contour"], ["rect", "Rectangle"], ["rounded", "Rounded"], ["circle", "Circle"], ["oval", "Oval"]];
export const SHEETS = [
  { id: "letter", name: "Letter 8½ × 11", w: 8.5, h: 11 },
  { id: "legal", name: "Legal 8½ × 14", w: 8.5, h: 14 },
  { id: "tabloid", name: "Tabloid 11 × 17", w: 11, h: 17 },
  { id: "12x18", name: "12 × 18", w: 12, h: 18 },
  { id: "13x19", name: "13 × 19", w: 13, h: 19 },
  { id: "roll24", name: "24″ roll", w: 24, h: 0 },
  { id: "roll30", name: "30″ roll", w: 30, h: 0 },
  { id: "roll54", name: "54″ roll", w: 54, h: 0 },
];
export const SHEET_MARGIN = 0.5; // clear zone for registration marks + grip
export const SIZE_PRESETS = [[2, 2], [3, 3], [4, 4], [5, 5], [6, 6], [3, 2], [4, 3], [6, 4], [11.5, 3]];
export const FONTS = ["Bebas Neue", "Anton", "Oswald", "Montserrat", "Poppins", "Russo One", "Black Ops One", "Bangers", "Righteous", "Bungee", "Lilita One", "Alfa Slab One", "Abril Fatface", "Playfair Display", "Roboto Slab", "Pacifico", "Lobster", "Permanent Marker", "Caveat", "Shrikhand", "Monoton", "Rye", "Press Start 2P", "Fredoka"];
export const SWATCHES = ["#111111", "#ffffff", "#e5484d", "#ff8a00", "#ffd60a", "#30d158", "#0a84ff", "#5e5ce6", "#bf5af2", "#ff375f", "#8e8e93", "#5b3a1a"];
export const LAYER_SHAPES = [["rect", "Square"], ["rounded", "Rounded"], ["circle", "Circle"], ["triangle", "Triangle"], ["star", "Star"], ["heart", "Heart"], ["hexagon", "Hexagon"], ["badge", "Badge"]];

export const newSticker = (type = "die-cut") => {
  const t = STICKER_TYPES.find((x) => x.id === type) || STICKER_TYPES[0];
  return {
    id: uid(), name: `${t.name} sticker`, type: t.id, w: t.w, h: t.h, shape: t.shape, radius: t.radius ?? 0.25, offset: 0.1, bleed: 0.125,
    material: t.material || "white-gloss", finish: "gloss", bg: null, border: null, layers: [], copies: t.copies || 1, sheet: t.sheet || "letter", gap: 0.125, cut: null,
  };
};
export const applyType = (doc, type) => {
  const t = STICKER_TYPES.find((x) => x.id === type) || STICKER_TYPES[0];
  const fresh = !doc.layers.length;
  return { ...doc, type: t.id, shape: t.shape, radius: t.radius ?? doc.radius, copies: t.copies || doc.copies, sheet: t.sheet || doc.sheet, material: t.material || doc.material, ...(fresh ? { w: t.w, h: t.h } : {}) };
};

export const newText = (doc, text = "YOUR TEXT") => ({
  id: uid(), kind: "text", text, font: "Bebas Neue", size: Math.max(0.25, Math.min(doc.w / 4, doc.h / 3)), fill: "#111111", stroke: "#ffffff", strokeWidth: 0,
  x: doc.w / 2, y: doc.h / 2, rot: 0, spacing: 0, weight: 400, italic: false, opacity: 1, lineHeight: 1.1,
});
export const newShape = (doc, shape = "rect") => {
  const s = Math.min(doc.w, doc.h) * 0.6;
  return { id: uid(), kind: "shape", shape, fill: "#e5484d", stroke: "#111111", strokeWidth: 0, x: doc.w / 2, y: doc.h / 2, w: s, h: s, rot: 0, opacity: 1, radius: 0.2 };
};
export const newImage = (doc, art) => {
  const k = Math.min((doc.w * 0.85) / art.w, (doc.h * 0.85) / art.h);
  return { id: uid(), kind: "image", artId: art.id, url: art.url, natural: { w: art.w, h: art.h }, x: doc.w / 2, y: doc.h / 2, w: art.w * k, h: art.h * k, rot: 0, opacity: 1 };
};
export const materialOf = (doc) => MATERIALS.find((m) => m.id === doc.material) || MATERIALS[0];
export const sheetOf = (doc) => SHEETS.find((s) => s.id === doc.sheet) || SHEETS[0];
export const typeOf = (doc) => STICKER_TYPES.find((t) => t.id === doc.type) || STICKER_TYPES[0];
export const fmtIn = (v) => `${Math.round(v * 100) / 100}″`;
